import React, { useState } from "react";
import { Grid } from "@mui/material";
import { Search } from "cprime-foundational-components";
import * as style from "./ApplicationAccessHeadCard.module.scss";
import {
  updateCurrentStep,
  updateFeatureStatus,
} from "../../store/slices/featureGroupWithFeatureSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store/store";
import CustomSelect from "./CustomSelect";
import { MdOutlineFileUpload, MdOutlineFileDownload } from "react-icons/md";
import UploadRequestDialog from "../ApplicationAccess/UploadeRequest/UploadRequest";
import CloneAccessDialog from "../ApplicationAccess/CloneAccess/CloneAccess";
import { triggerToast } from "../../utils/toast";
import { downloadRequestTemplateApi } from "../../services/RequestAccess/RequestAccess";
import CprimeButton from "../../Components/Common/CprimeButton";
// import EditIcon from "@mui/icons-material/Edit";

interface RequestForAccessProps {
  getSearchValue?: (query: string) => void;
  onSubmit?: () => void;
  setSearchValue?: (value: string) => void;
  searchValue?: string;
  currentStep?: number;
  isSearch?: boolean;
  applicationDomain?: any;
  selectedDomain?: any;
  setSelectedDomain?: any;
  children: React.ReactNode;
  isAdminOrSuperAdmin?: boolean;
  setIsAddingUser?: any;
  isAddingUser?: boolean;
}

const RequestForAccess: React.FC<RequestForAccessProps> = ({
  getSearchValue,
  onSubmit,
  setSearchValue,
  searchValue,
  currentStep = 1,
  isSearch,
  applicationDomain,
  selectedDomain,
  setSelectedDomain,
  children,
  isAddingUser = false,
  setIsAddingUser,
}) => {
  const [uploadRequestDialogIsOpen, setUploadRequestDialogIsOpen] =
    useState<boolean>(false);
  const [cloneAccessDialogIsOpen, setCloneAccessDialogIsOpen] =
    useState<boolean>(false);

  const scssClass = style as any;
  const dispatch = useDispatch<AppDispatch>();

  const handleSearchChange = (value: string) => {
    setSearchValue?.(value);
    getSearchValue?.(value);
  };

  const handleSelectionChange = (selectedValues: any) => {
    if (
      JSON.stringify(selectedDomain) !== JSON.stringify(selectedValues) &&
      selectedValues?.app_id?.length > 0 &&
      selectedValues?.value_stream_id?.length > 0
    ) {
      setSelectedDomain?.(selectedValues);
    }
  };

  const downloadRequestTemplate = async () => {
    try {
      // const fileUrl = `${getEnv()?.childAppUri}/files/requesttemplate.xlsx`; // Path relative to the public folder
      const response = (await downloadRequestTemplateApi()) as any;
      if (response.status !== 200) {
        triggerToast(
          "Failed to download template. Check token or permissions.",
          "error",
        );
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const fileUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = "template.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      triggerToast("Request template downloaded successfully", "success");
    } catch (err) {}
  };

  return (
    <>
      <div className={scssClass.ApplicationAccessHeadCardContainer}>
        <div className={scssClass.requestAccessContainer}>
          {/* Header Section */}
          <Grid container className={scssClass.header} spacing={1}>
            <Grid
              item
              xs={12}
              style={{
                display: "flex",
                // flexDirection: "column",
                justifyContent: "space-between",
                padding: "1rem",
                flexWrap: "wrap",
                gap: "12px 0px",
              }}
            >
              <div style={{ display: "grid" }}>
                <span className={scssClass.title}>Request for Access</span>
                <span className={scssClass.subTitle}>
                  Step {!isAddingUser ? +currentStep - 1 : currentStep} of{" "}
                  {!isAddingUser ? 2 : 3}:{" "}
                  {currentStep === 1
                    ? "Select User(s) to request access on behalf of."
                    : currentStep === 2
                      ? "Select Features"
                      : "Select permissions and add justifications"}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px 4px",
                  flexWrap: "wrap",
                }}
              >
                {currentStep === 1 ? (
                  <>
                    <span
                      role="button"
                      data-testid="requestTemplateId"
                      onKeyDown={() => {}}
                      tabIndex={0}
                      className={scssClass.requestTemplateButton}
                      onClick={downloadRequestTemplate}
                    >
                      <span className={scssClass.uploadRequestButton}>
                        <p>Request Template</p>
                        <MdOutlineFileDownload style={{ fontSize: "20px" }} />
                      </span>
                    </span>

                    <CprimeButton
                      variant="secondary"
                      onClick={() => setUploadRequestDialogIsOpen(true)}
                      sx={{ height: "40px" }}
                    >
                      <span className={scssClass.uploadRequestButton}>
                        <p>Bulk Upload Request</p>
                        <MdOutlineFileUpload style={{ fontSize: "20px" }} />
                      </span>
                    </CprimeButton>
                  </>
                ) : currentStep === 2 ? (
                  <>
                    {!isAddingUser ? (
                      <CprimeButton
                        variant="tertiary"
                        sx={{ marginLeft: "4px", marginRight: "4px" }}
                        onClick={() => {
                          setIsAddingUser(true);
                          dispatch(updateCurrentStep(1));
                        }}
                      >
                        Request for Others
                      </CprimeButton>
                    ) : null}
                    <CprimeButton
                      variant="tertiary"
                      sx={{ marginLeft: "4px", marginRight: "4px" }}
                      onClick={() => setCloneAccessDialogIsOpen(true)}
                    >
                      Clone Access
                    </CprimeButton>
                  </>
                ) : // <Button
                //   label={
                //     <div className={scssClass.editIconButton}>
                //       Edit Features <EditIcon sx={{ fontSize: "1rem" }} />
                //     </div>
                //   }
                //   variant="tertiary"
                //   sx={{ marginLeft: "4px", marginRight: "4px" }}
                //   onClick={() => {
                //     dispatch(updateCurrentStep(2));
                //   }}
                // />
                null}
              </div>
            </Grid>
          </Grid>
          {isSearch && <div className={scssClass.divider} />}
          {/* Body Section */}
          {isSearch && (
            <Grid
              container
              className={scssClass.body}
              alignItems="center"
              spacing={2}
            >
              <Grid
                item
                xs={12}
                className={scssClass.bodyFirstDiv}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  paddingLeft: "0",
                  paddingTop: "0",
                }}
              >
                <div className={scssClass.domainFilterContainer}>
                  {Array.isArray(applicationDomain) &&
                    applicationDomain?.length > 0 && (
                      <CustomSelect
                        valueStreams={applicationDomain}
                        onChange={handleSelectionChange}
                        selectedOptions={selectedDomain ?? []}
                        optionsSx={{
                          minWidth: "190px",
                          maxWidth: "390px !important",
                          maxHeight: "22rem", // Add this line
                          "& .MuiFormControl-root": {
                            marginTop: "0px !important",
                          },
                          "& .MuiMenuItem-root": {
                            padding: "1rem",
                            backgroundColor: "transparent !important",
                          },
                          "& .MuiMenuItem-root:hover": {
                            backgroundColor: "transparent !important",
                          },
                          "& .MuiMenuItem-root.Mui-selected": {
                            backgroundColor: "transparent !important",
                          },
                          "& .MuiMenuItem-root.Mui-selected:hover": {
                            backgroundColor: "transparent !important",
                          },
                          "& .MuiPaper-root": {
                            maxHeight: "22rem", // Also add this for the dropdown paper container
                            overflow: "auto",
                            borderColor: "black", // Enable scrolling if content exceeds height
                          },
                        }}
                      />
                    )}
                  {/* <CprimeButton
                    variant="secondary"
                    onClick={() => {
                      dispatch(updateSelectedDomains(selectedDomain));
                      dispatch(updateFeatureStatus("idle"));
                    }}
                  >
                    Apply
                  </CprimeButton> */}
                  <CprimeButton
                    variant="tertiary"
                    onClick={() => {
                      setSelectedDomain({
                        app_id: [],
                        value_stream_id: [],
                      });
                      dispatch(updateFeatureStatus("idle"));
                    }}
                  >
                    Reset
                  </CprimeButton>
                </div>
                <Search
                  autoFocus={false}
                  value={searchValue || ""}
                  placeholder={"Search"}
                  onChange={handleSearchChange}
                  onSubmit={onSubmit || (() => {})}
                  onClear={() => {
                    setSearchValue?.("");
                    getSearchValue?.("");
                  }}
                  className={scssClass.search}
                  sx={{
                    ".MuiButtonBase-root.MuiIconButton-root": {
                      padding: "0.5rem 0.75rem 0.5rem 0rem",
                      fontSize: "1.5rem",
                    },
                  }}
                />
              </Grid>
            </Grid>
          )}
        </div>

        {/* Children Section */}
        {children && (
          <Grid container className={scssClass.childrenContainer}>
            <Grid item xs={12}>
              {children}
            </Grid>
          </Grid>
        )}
      </div>
      {uploadRequestDialogIsOpen && (
        <UploadRequestDialog
          open={uploadRequestDialogIsOpen}
          onClose={() => setUploadRequestDialogIsOpen(false)}
        />
      )}
      {cloneAccessDialogIsOpen && (
        <CloneAccessDialog
          open={cloneAccessDialogIsOpen}
          onClose={() => setCloneAccessDialogIsOpen(false)}
        />
      )}
    </>
  );
};

export default RequestForAccess;
